import { deleteProfileImage, getUserProfile, patchUser, updateProfileImage } from '@pequity/shared/api/auth';
import type { UserPatchPayload, UserProfile } from '@pequity/shared/types/user';
import { defineStore } from 'pinia';

const LOCALE_STORAGE_KEY = 'user_locale';

export const useAuthStore = defineStore('auth', {
  state: (): { loggedIn: boolean; profile: UserProfile | null } => ({
    loggedIn: false,
    profile: null,
  }),
  getters: {
    // The router guards load the profile before any authenticated route renders,
    // so authenticated code reads it from here; a read before that is a bug.
    currentProfile: (state): UserProfile => {
      if (!state.profile) throw new Error('The user profile is not loaded yet');
      return state.profile;
    },
    userId: (state) => state.profile?.id,
    isUserAdmin: (state) => !!state.profile?.is_admin,
    isUserTeamlead: (state) => !!state.profile?.is_teamlead,
    hasPeopleAccess: (state) => !!state.profile?.has_people_access,
    appAccess(): UserProfile['app_access'] {
      return this.currentProfile.app_access;
    },
    isUserStaff: (state) => !!state.profile?.is_staff,
    isStaffMode: (state) => state.loggedIn && !!state.profile?.staff_mode,
  },
  actions: {
    async setUserProfileAndLoggedInState() {
      const { data, error } = await getUserProfile();

      if (!error) {
        this.profile = data;
        this.loggedIn = true;
      }
    },
    async updateProfileImage(image: File) {
      const { error } = await updateProfileImage(this.currentProfile.id, image);
      if (!error) {
        await this.setUserProfileAndLoggedInState();
      }
    },
    async deleteProfileImage() {
      const { error } = await deleteProfileImage(this.currentProfile.id);
      if (!error) {
        await this.setUserProfileAndLoggedInState();
      }
    },
    async changeProfileInfo(data: UserPatchPayload) {
      const { error, response } = await patchUser(this.currentProfile.id, data);
      if (!error) {
        if (data.locale) {
          localStorage.setItem(LOCALE_STORAGE_KEY, data.locale);
        }

        this.profile = { ...this.currentProfile, ...response.data };
      }
    },
  },
});
